import { Component, Input, ViewEncapsulation } from '@angular/core';

@Component({
	selector: 'portal-button',
	template: `
		<button class="portal-btn"
			[attr.type]="type"
			[disabled]="disabled"
			[class.portal-btn-primary]="variant === 'primary'"
			[class.portal-btn-icon]="!!icon"
			[attr.title]="title"
			[attr.aria-label]="ariaLabel">
			<i *ngIf="icon" [class]="icon"></i><ng-content></ng-content>
		</button>
	`,
	encapsulation: ViewEncapsulation.None,
	host: {
		'[class.portal-button-block]': 'block'
	},
	styles: [`
		portal-button {
			display: inline-flex;
		}

		portal-button.portal-button-block {
			width: 100%;
		}

		.portal-btn {
			flex: 1 1 auto;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			gap: 0.3rem;
			border: 1px solid var(--c-border-3);
			border-radius: 0.2rem;
			background: var(--c-surface-2);
			padding: 0.4rem 0.8rem;
			font-family: inherit;
			font-size: 0.8rem;
			font-weight: 600;
			color: var(--c-text-1);
			cursor: pointer;
			white-space: nowrap;
		}

		.portal-btn:hover:not(:disabled) {
			background: var(--c-hover);
		}

		.portal-btn:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		.portal-btn-icon {
			padding-left: 0.55rem;
			padding-right: 0.55rem;
		}

		.portal-btn-primary {
			background: var(--c-primary-solid);
			border-color: var(--c-primary-solid);
			color: #fff;
		}

		.portal-btn-primary:hover:not(:disabled) {
			filter: brightness(0.95);
		}

		/* Segmented control: connected buttons sharing one outer border. */
		.portal-btn-group {
			display: inline-flex;
			border: 1px solid var(--c-border-3);
			border-radius: 0.2rem;
			overflow: hidden;
		}

		.portal-btn-group .portal-btn {
			border: none;
			border-radius: 0;
		}

		.portal-btn-group portal-button:not(:first-child) .portal-btn {
			border-left: 1px solid var(--c-border-3);
		}

		@media (max-width: 1024px) {
			.portal-btn-group {
				width: 100%;
			}

			.portal-btn-group portal-button {
				flex: 1;
			}
		}
	`]
})
export class PortalButtonComponent {
	@Input() variant: 'default' | 'primary' = 'default';
	@Input() icon: string | null = null;
	@Input() type = 'button';
	@Input() title: string | null = null;
	@Input('aria-label') ariaLabel: string | null = null;

	// Coerce so a bare attribute (<portal-button disabled>) counts as true —
	// Angular 14 otherwise binds it as an empty string, which is falsy.
	private _disabled = false;
	private _block = false;

	@Input()
	get disabled(): boolean { return this._disabled; }
	set disabled(value: any) { this._disabled = value != null && `${value}` !== 'false'; }

	@Input()
	get block(): boolean { return this._block; }
	set block(value: any) { this._block = value != null && `${value}` !== 'false'; }
}
